import React, { useState, useEffect, useRef } from 'react';
import { useAuth } from '../context/authcontext';
import { getApiBaseUrl } from '../utils/apiConfig';
import { isNative } from '../utils/platform';
import { Bell, X } from 'lucide-react';
import { motion, AnimatePresence } from 'framer-motion';

const CONSENT_KEY = 'nativus_notif_consent';
const DISMISS_KEY = 'nativus_notif_dismiss_at';
const COOLDOWN_DAYS = 14;
const COOLDOWN_MS = COOLDOWN_DAYS * 24 * 60 * 60 * 1000;

function hasConsented() {
  return localStorage.getItem(CONSENT_KEY) === 'granted';
}

function isDismissedRecently() {
  const ts = parseInt(localStorage.getItem(DISMISS_KEY) || '0', 10);
  return ts > 0 && Date.now() - ts < COOLDOWN_MS;
}

function recordDismiss() {
  localStorage.setItem(DISMISS_KEY, String(Date.now()));
}

function recordConsent() {
  localStorage.setItem(CONSENT_KEY, 'granted');
}


async function requestOSPermission(userId) {
  if (isNative()) {
    const { registerNativePushNotifications } = await import('../services/notifications');
    await registerNativePushNotifications(userId);
    const { checkNativePushPermission } = await import('../services/notifications');
    return await checkNativePushPermission();
  } else {
    const { requestNotificationPermission, registerFCMToken } = await import('../services/notifications');
    const perm = await requestNotificationPermission();
    if (perm === 'granted' && userId) {
      registerFCMToken(userId).catch(() => {});
    }
    return perm;
  }
}

// Prima vizită pe Home în sesiune → timer 30s; pop-up-ul apare oriunde în app.
export const HOME_FIRST_VISIT_KEY = 'nativus_home_first_visit_at';
export const HOME_FIRST_VISIT_EVENT = 'nativus:home-first-visit';
const PROMPT_DEADLINE_KEY = 'nativus_notif_prompt_deadline';
const PROMPT_DELAY_MS = 30_000;

export function markHomeFirstVisit() {
  if (!sessionStorage.getItem(HOME_FIRST_VISIT_KEY)) {
    const now = Date.now();
    sessionStorage.setItem(HOME_FIRST_VISIT_KEY, String(now));
    sessionStorage.setItem(PROMPT_DEADLINE_KEY, String(now + PROMPT_DELAY_MS));
    window.dispatchEvent(new CustomEvent(HOME_FIRST_VISIT_EVENT));
  }
}

const NotificationPrompt = () => {
  const { user } = useAuth();
  const userRef = useRef(user);
  userRef.current = user;
  // 'hidden' | 'prompt' | 'settings-guide'
  const [view, setView] = useState('hidden');
  const [isLoading, setIsLoading] = useState(false);

  useEffect(() => {
    const userId = user?.id;
    if (!userId || hasConsented() || isDismissedRecently()) return undefined;

    let cancelled = false;
    let timeoutId = null;

    const showPrompt = () => {
      if (cancelled || !userRef.current?.id || hasConsented() || isDismissedRecently()) return;
      setView('prompt');
    };

    const armTimer = () => {
      const visitAt = sessionStorage.getItem(HOME_FIRST_VISIT_KEY);
      if (!visitAt) return false;

      let deadline = parseInt(sessionStorage.getItem(PROMPT_DEADLINE_KEY) || '0', 10);
      if (!deadline) {
        deadline = parseInt(visitAt, 10) + PROMPT_DELAY_MS;
        sessionStorage.setItem(PROMPT_DEADLINE_KEY, String(deadline));
      }

      const remaining = Math.max(0, deadline - Date.now());
      if (timeoutId) clearTimeout(timeoutId);
      timeoutId = setTimeout(showPrompt, remaining);
      return true;
    };

    const onHomeFirstVisit = () => {
      armTimer();
    };

    if (!armTimer()) {
      window.addEventListener(HOME_FIRST_VISIT_EVENT, onHomeFirstVisit);
    }

    return () => {
      cancelled = true;
      if (timeoutId) clearTimeout(timeoutId);
      window.removeEventListener(HOME_FIRST_VISIT_EVENT, onHomeFirstVisit);
    };
  }, [user?.id]);

  const handleEnable = async () => {
    setIsLoading(true);
    try {
      // Save GDPR consent + preferences in background
      recordConsent();
      const { supabase } = await import('../lib/supabase');
      const { data: { session } } = await supabase.auth.getSession();
      const token = session?.access_token;
      const authHeader = token ? { 'Authorization': `Bearer ${token}` } : {};
      fetch(`${getApiBaseUrl()}/api/notification-preferences`, {
        method: 'PUT',
        headers: { 'Content-Type': 'application/json', ...authHeader },
        body: JSON.stringify({
          reminder_enabled: true,
          reminder_time: '21:30',
          timezone: Intl.DateTimeFormat().resolvedOptions().timeZone,
          marketing_enabled: true,
          consented_at: new Date().toISOString(),
        }),
      }).catch(() => {});

      // Now trigger the OS native dialog
      const perm = await requestOSPermission(user?.id);

      if (perm === 'granted') {
        setView('hidden');
      } else {
        // User accepted ours but denied at OS level → guide to Settings
        setView('settings-guide');
      }
    } catch {
      setView('hidden');
    } finally {
      setIsLoading(false);
    }
  };

  const handleDismiss = () => {
    recordDismiss();
    setView('hidden');
  };

  if (view === 'hidden' || !user) return null;

  return (
    <AnimatePresence>
      <motion.div
        key={view}
        initial={{ opacity: 0, y: 20 }}
        animate={{ opacity: 1, y: 0 }}
        exit={{ opacity: 0, y: 20 }}
        transition={{ duration: 0.25 }}
        className="fixed left-4 right-4 sm:left-auto sm:right-4 sm:max-w-md z-50"
        style={{ bottom: 'calc(var(--bottom-nav-clearance) + 0.75rem)' }}
      >
        <div className="bg-white rounded-2xl shadow-2xl p-4 border border-accent/20">
          {view === 'prompt' && (
            <div className="flex items-start gap-3">
              <div className="bg-accent/10 p-2 rounded-full flex-shrink-0">
                <Bell className="text-accent" size={24} />
              </div>
              <div className="flex-1">
                <h3 className="font-poppins font-semibold text-text-primary mb-1">
                  Activează notificările
                </h3>
                <p className="text-sm text-text-primary/80 font-poppins mb-3">
                  O invitație blândă. Nu ca să faci mai mult. Ci ca să-ți amintești de tine.
                </p>
                <div className="flex gap-2">
                  <button
                    onClick={handleEnable}
                    disabled={isLoading}
                    className="flex-1 px-4 py-2 bg-accent text-white font-semibold rounded-xl transition-all disabled:opacity-50 disabled:cursor-not-allowed"
                  >
                    {isLoading ? 'Se activează...' : 'Activează'}
                  </button>
                  <button
                    onClick={handleDismiss}
                    className="px-4 py-2 border border-primary/20 text-text-primary font-medium rounded-xl hover:bg-primary/5 transition-all"
                  >
                    <X size={18} />
                  </button>
                </div>
              </div>
            </div>
          )}

          {view === 'settings-guide' && (
            <div className="flex items-start gap-3">
              <div className="bg-accent/10 p-2 rounded-full flex-shrink-0">
                <Bell className="text-accent" size={24} />
              </div>
              <div className="flex-1">
                <p className="text-sm text-text-primary/80 font-poppins mb-3">
                  Pentru a rămâne aproape, activează notificările pentru Nativus din setările telefonului.
                </p>
                <button
                  onClick={() => setView('hidden')}
                  className="w-full px-4 py-2 bg-accent text-white font-semibold rounded-xl transition-all"
                >
                  Ok
                </button>
              </div>
            </div>
          )}
        </div>
      </motion.div>
    </AnimatePresence>
  );
};

export default NotificationPrompt;
